import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Grid,
    TextField, Switch, Button, Alert, CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import StorefrontIcon from "@mui/icons-material/Storefront";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";

const EditTierDialog = ({ open, tier, onClose, onSaved }) => {
    const [form, setForm] = useState({ key: "", name: "", min_status_points: 0, rank: 0, active: true });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (tier) {
            setForm({ key: tier.key || "", name: tier.name || "", min_status_points: tier.min_status_points ?? 0, rank: tier.rank ?? 0, active: tier.active ?? true });
            setError(null);
        }
    }, [tier]);

    const handleChange = (field) => (e) => {
        const value = e.target.type === "number" ? Number(e.target.value) : e.target.value;
        setForm((p) => ({ ...p, [field]: value }));
    };

    const isValid = form.key.trim() && form.name.trim();

    const handleSubmit = async () => {
        if (!isValid || !tier) return;
        setSubmitting(true);
        setError(null);
        try {
            const res = await fetch(`${LOYALTY_API_BASE}/admin/loyalty-tiers/${tier.id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "X-Brand": tier.brand
                },
                body: JSON.stringify({ key: form.key.trim(), name: form.name.trim(), min_status_points: form.min_status_points, rank: form.rank, active: form.active }),
            });
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(e?.detail || `Erreur HTTP ${res.status}`);
            }
            onSaved();
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (!tier) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.tiers.gradient }}>
                        <EditIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>Modifier le Niveau</Typography>
                        <Typography fontSize={11} color="text.secondary">{tier.brand} • {tier.name}</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2.5} px={1.5} py={1}
                    sx={{ borderRadius: 2, bgcolor: "#f8f9ff", border: "1px solid #e0e7ff" }}>
                    <StorefrontIcon sx={{ fontSize: 18, color: SECTION_COLORS.tiers.main }} />
                    <Typography fontSize={13} color="text.secondary">Marque :</Typography>
                    <Chip label={tier.brand} size="small" sx={{ fontWeight: 700, fontSize: 12, bgcolor: `${SECTION_COLORS.tiers.main}15`, color: SECTION_COLORS.tiers.main }} />
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth required label="Clé unique" value={form.key} onChange={handleChange("key")} size="small"
                            helperText="Identifiant technique" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth required label="Nom du niveau" value={form.name} onChange={handleChange("name")} size="small"
                            helperText="Nom affiché aux utilisateurs" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Points minimum" type="number" value={form.min_status_points}
                            onChange={handleChange("min_status_points")} size="small" helperText="Points requis pour ce niveau"
                            InputProps={{ inputProps: { min: 0 } }} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Rang" type="number" value={form.rank}
                            onChange={handleChange("rank")} size="small" helperText="Ordre d'affichage (1 = plus bas)"
                            InputProps={{ inputProps: { min: 0 } }} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Box>
                                <Typography fontSize={13} fontWeight={600}>Activer ce niveau</Typography>
                                <Typography fontSize={11} color="text.secondary">Disponible immédiatement pour les clients</Typography>
                            </Box>
                            <Switch checked={form.active} onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.tiers.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.tiers.main }
                                }} />
                        </Box>
                    </Grid>
                </Grid>

                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    sx={{ background: SECTION_COLORS.tiers.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    {submitting ? "Enregistrement..." : "Enregistrer"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditTierDialog;
